import React, { useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import JsonCollapse from "../JsonCollapse";
import TokenEditor from "../TokenEditor";
import UsedInLink from "../UsedInLink";
import {
  addNewMessageConfig,
  selectInputArguments,
  selectMessageConfig,
  selectProjectMessageConfigUUID,
  setMessageConfig as setMessageCfg,
  setProjectMessageConfigUUID,
} from "../../../store/slices/projectSlice";
import DynamicHeaderInput from "../../nodes/Logic/Inputs/DynamicHeaderInput";
import { useGetCommsSettingsDataQuery } from "../../../store/slices/settingsSlice";

export default function MessageConfig() {
  const navigate = useNavigate();
  const params = useParams();
  const pageProps = JSON.parse(
    document.querySelector("#app").dataset.page
  ).props;

  const inputArguments = useSelector(selectInputArguments);
  const projectMessageConfigUUID = useSelector(selectProjectMessageConfigUUID);
  const messageConfig = useSelector(
    selectMessageConfig(params.messageConfigUUID)
  );
  const dispatch = useDispatch();
  const setMessageConfig = (fn) => dispatch(setMessageCfg(fn(messageConfig)));

  const location = useLocation();
  const handleClose = () => {
    if (messageConfig?.Label) {
      navigate(
        location.state?.nextPage || `/library/logic/builder/${params.projectID}`
      );
    } else {
      toast.error("Label is missing!");
    }
  };
  const { data: commsSettings } = useGetCommsSettingsDataQuery();

  const labelRef = useRef();
  useEffect(() => {
    // set messageConfig when the window shows
    if (params.messageConfigUUID) {
      if (params.messageConfigUUID === "create_new") {
        const newMCUUID = uuidv4();
        dispatch(addNewMessageConfig({ UUID: newMCUUID, commsSettings }));
        navigate(
          `/library/logic/builder/${params.projectID}/message-config/${newMCUUID}`,
          {
            replace: true,
          }
        );
      }
    }
  }, [params.messageConfigUUID]);

  if (messageConfig) {
    return (
      <Modal
        size="xl"
        centered
        show
        onEntered={() => labelRef.current.select()}
        onHide={handleClose}
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div>
              {params.messageConfigUUID ? "Edit " : "Create "} message config
            </div>{" "}
            <div className="flex-grow-1" />
          </Modal.Title>
          <span className="flex-grow-1" />
          {projectMessageConfigUUID === messageConfig?.UUID ? (
            <span className="badge bg-success me-1">Default</span>
          ) : (
            <button
              type="button"
              className="btn btn-link text-white btn-sm me-1"
              onClick={() =>
                dispatch(setProjectMessageConfigUUID(messageConfig.UUID))
              }
            >
              Set as project default
            </button>
          )}
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="mb-3 col-6">
              <label className="form-label">Label</label>
              <input
                ref={labelRef}
                className="form-control"
                title="Message config label"
                placeholder="Message config label"
                type="text"
                value={messageConfig?.Label ?? ""}
                onChange={(e) =>
                  setMessageConfig((prev) => ({
                    ...prev,
                    Label: e.target.value,
                  }))
                }
              />
            </div>
            <div className="mb-3 col-6">
              <label className="form-label">Email responses to</label>
              <input
                className="form-control"
                title="Message config EmailResponsesTo"
                placeholder="Message config EmailResponsesTo"
                type="text"
                value={messageConfig?.EmailResponsesTo ?? ""}
                onChange={(e) =>
                  setMessageConfig((prev) => ({
                    ...prev,
                    EmailResponsesTo: e.target.value,
                  }))
                }
              />
            </div>
            <div className="mb-3 col-6">
              <TokenEditor
                label="Email from"
                showAiButton={false}
                showShortlinksButton={false}
                tokenTags={[
                  "contact_field",
                  "input_argument",
                  "input_argument_count",
                  "workflow_name",
                  "guid",
                  "component_name",
                  "current_time",
                  "customer_name",
                ]}
                initialText={`Email From ${pageProps.productName}`}
                rawEditorState={messageConfig?.EditorStateEmailFrom}
                inputArgumentsFilter={(arg) => !arg.IsMultiple}
                inputArguments={inputArguments}
                onChange={({ rawEditorState, HTMLtext, tokens }) =>
                  setMessageConfig((prev) => ({
                    ...prev,
                    EmailFrom: HTMLtext,
                    EditorStateEmailFrom: rawEditorState,
                    Tokens: [
                      ...(prev.Tokens || []).filter(
                        (token) => token.UsedIn !== "EmailFrom"
                      ),
                      ...tokens.map((token) => ({
                        ...token,
                        UsedIn: "EmailFrom",
                      })),
                    ],
                  }))
                }
              />
            </div>
            <div className="mb-3 col-6">
              <TokenEditor
                label="Subject"
                showAiButton={false}
                showShortlinksButton={false}
                tokenTags={[
                  "contact_field",
                  "input_argument",
                  "input_argument_count",
                  "workflow_name",
                  "guid",
                  "component_name",
                  "current_time",
                  "customer_name",
                ]}
                initialText={`Email From ${pageProps.productName}`}
                inputArgumentsFilter={(arg) => !arg.IsMultiple}
                inputArguments={inputArguments}
                rawEditorState={messageConfig?.EditorStateSubject}
                onChange={({ rawEditorState, HTMLtext, tokens }) =>
                  setMessageConfig((prev) => ({
                    ...prev,
                    Subject: HTMLtext,
                    EditorStateSubject: rawEditorState,
                    Tokens: [
                      ...(prev.Tokens || []).filter(
                        (token) => token.UsedIn !== "EmailSubject"
                      ),
                      ...tokens.map((token) => ({
                        ...token,
                        UsedIn: "EmailSubject",
                      })),
                    ],
                  }))
                }
              />
            </div>
            <div className="mb-3 col-12 small-editor">
              <TokenEditor
                label="Signature"
                showAiButton={false}
                showShortlinksButton={false}
                showTokens={false}
                tokenTags={[]}
                initialText={`Email signature ${pageProps.productName}`}
                inputArgumentsFilter={(arg) => !arg.IsMultiple}
                inputArguments={inputArguments}
                rawEditorState={messageConfig?.EditorStateEmailSignature}
                onChange={({ rawEditorState, HTMLtext, tokens }) =>
                  setMessageConfig((prev) => ({
                    ...prev,
                    Signature: HTMLtext,
                    EditorStateEmailSignature: rawEditorState,
                    Tokens: [
                      ...(prev.Tokens || []).filter(
                        (token) => token.UsedIn !== "EmailSignature"
                      ),
                      ...tokens.map((token) => ({
                        ...token,
                        UsedIn: "EmailSignature",
                      })),
                    ],
                  }))
                }
              />
            </div>
            <div className="mb-3 col-6">
              <label className="form-label">Dynamic header</label>
              <DynamicHeaderInput
                placeholder="Message config DynamicHeader"
                value={messageConfig?.DynamicHeader ?? ""}
                onChange={(v) =>
                  setMessageConfig((prev) => ({
                    ...prev,
                    DynamicHeader: v,
                  }))
                }
              />
            </div>
          </div>
          <UsedInLink arg={messageConfig} />
          <JsonCollapse json={messageConfig} />
        </Modal.Body>
      </Modal>
    );
  }
}
